import { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, TrendingUp, PhoneCall, Users } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/hero-marke.jpg";

const CHIP_ICONS = [PhoneCall, Users, TrendingUp];

const Simulator = () => {
  const { t } = useLanguage();
  const s = t.hero.sim;
  const [care, setCare] = useState(0);
  const [city, setCity] = useState(1);
  const [current, setCurrent] = useState(8);

  const potential = useMemo(() => {
    const cityFactor = [2.4, 3.2, 4.0][city];
    const careBonus = care === 2 ? 1.1 : 1.0; // Intensivpflege: höhere Nachfrage
    return Math.max(6, Math.round(current * cityFactor * careBonus));
  }, [care, city, current]);

  return (
    <div data-testid="hero-simulator" className="mt-8 rounded-[3px] border border-bone/10 bg-carddark/80 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-4">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{s.title}</p>
        <p className="hidden text-xs text-inkmuted sm:block">{s.sub}</p>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="sim-care" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-inkmuted">{s.careType}</label>
          <select
            id="sim-care"
            data-testid="simulator-care-select"
            value={care}
            onChange={(e) => setCare(Number(e.target.value))}
            className="w-full rounded-[3px] border border-bone/15 bg-dark px-3 py-2.5 text-sm text-bone outline-none transition-colors focus:border-gold"
          >
            {s.careOptions.map((o, i) => <option key={o} value={i}>{o}</option>)}
          </select>
          <p className="mt-2 mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-inkmuted">{s.city}</p>
          <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={s.city}>
            {s.cityOptions.map((o, i) => (
              <button
                key={o}
                type="button"
                data-testid={`simulator-city-${i}`}
                onClick={() => setCity(i)}
                className={`rounded-[3px] border px-3 py-1.5 text-left text-xs transition-colors ${city === i ? "border-gold text-bone" : "border-bone/10 text-bone/50 hover:border-bone/30 hover:text-bone/80"}`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <label htmlFor="sim-current" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-inkmuted">{s.current}</label>
          <input
            id="sim-current"
            data-testid="simulator-current-range"
            type="range"
            min="0"
            max="40"
            value={current}
            onChange={(e) => setCurrent(Number(e.target.value))}
            className="mt-3 w-full accent-gold"
          />
          <p className="mt-1 font-display text-2xl font-bold text-bone">{current}</p>
          <p className="text-xs text-inkmuted">{s.currentHint}</p>
          <div className="mt-auto pt-4">
            <div className="rounded-[3px] border border-gold/30 bg-gold/5 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{s.resultLabel}</p>
              <p data-testid="simulator-result" className="font-display text-3xl font-bold text-bone">
                ~ {potential} <span className="text-sm font-normal text-bone/60">{s.perMonth}</span>
              </p>
            </div>
            <p className="mt-2 text-[11px] leading-snug text-inkmuted">{s.resultHint}</p>
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="simulator-cta"
        onClick={() => scrollToId("kontakt")}
        className="mt-4 inline-flex items-center gap-2 rounded-[3px] border border-gold/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink"
      >
        {s.cta}
        <ArrowRight size={14} />
      </button>
    </div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.07]);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.13, delayChildren: 0.25 } },
  };
  const line = {
    hidden: { y: "115%" },
    show: { y: 0, transition: { duration: 1.05, ease: EASE } },
  };

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-[76px]">
      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 pb-16 pt-10 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pt-20 lg:pb-24">
        <motion.div style={{ y: yText }} className="z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" aria-hidden="true" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{t.hero.eyebrow}</p>
          </motion.div>

          <motion.h1 variants={container} initial="hidden" animate="show" className="font-display text-4xl leading-[1.14] tracking-tight sm:text-5xl lg:text-6xl">
            <span className="mask-line"><motion.span variants={line} className="block">{t.hero.l1}</motion.span></span>
            <span className="mask-line"><motion.span variants={line} className="block">{t.hero.l2}</motion.span></span>
            <span className="mask-line"><motion.span variants={line} className="block text-gold">{t.hero.l3}</motion.span></span>
            <span className="mask-line"><motion.span variants={line} className="block text-gold">{t.hero.l4}</motion.span></span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-inkmuted sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              type="button"
              data-testid="hero-cta-main"
              onClick={() => scrollToId("kontakt")}
              className="group inline-flex items-center justify-center gap-2 rounded-[3px] bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
            >
              {t.hero.cta1}
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("leistungen")}
              className="inline-flex items-center justify-center gap-2 rounded-[3px] border border-bone/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-bone transition-colors hover:border-gold hover:text-gold"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            {t.hero.chips.map((c, i) => {
              const Icon = CHIP_ICONS[i] || TrendingUp;
              return (
                <span key={c} className="inline-flex items-center gap-2 rounded-full border border-bone/15 bg-carddark/70 px-4 py-2 text-xs text-bone/80">
                  <Icon size={13} className="text-gold" />
                  {c}
                </span>
              );
            })}
          </motion.div>

          <Simulator />
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            className="relative overflow-hidden rounded-[3px] border border-bone/10"
          >
            <motion.img
              src={HERO_IMG}
              alt={t.hero.imgAlt}
              style={{ scale: imgScale }}
              className="h-[420px] w-full object-cover lg:h-[560px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent" aria-hidden="true" />
          </motion.div>
          <div className="mt-6 hidden border-l-2 border-gold pl-5 lg:block">
            <p className="text-sm leading-relaxed text-bone/70">{t.hero.side}</p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-gold">{t.hero.meta}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
